import { jsPDF } from 'jspdf';

/**
 * Génère et télécharge un reçu PDF (format A5 paysage) pour une cotisation validée.
 *
 * Le reçu reprend les conventions visuelles de l'app : en-tête coloré (emerald),
 * montant mis en avant, et mention de validation. Le numéro de reçu correspond
 * à l'identifiant Firestore de la cotisation.
 *
 * @param {object} contribution - La cotisation { id, userName, amount, type, paymentMethod, createdAt, validatedBy, validatedAt }
 * @param {object} options
 * @param {string} options.dahiraName - Nom du Dahira affiché dans l'en-tête (défaut: 'Daara Wakana')
 */
export const generateReceipt = (contribution, { dahiraName = 'Daara Wakana' } = {}) => {
  if (!contribution) return;

  // Format A5 paysage (210 x 148 mm) pour un reçu compact et propre
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a5' });
  const pageWidth = pdf.internal.pageSize.getWidth();   // ~210
  const pageHeight = pdf.internal.pageSize.getHeight();  // ~148

  // Helpers de formatage
  const toJsDate = (raw) => {
    if (!raw) return null;
    if (raw.toDate) return raw.toDate();
    return new Date(raw);
  };
  const formatDate = (raw) => {
    const d = toJsDate(raw);
    return d ? d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : 'N/A';
  };
  const paymentLabel = (m) =>
    m === 'wave' ? 'Wave' : m === 'orange_money' ? 'Orange Money' : 'Espèces';

  // ---- En-tête coloré (emerald) ----
  pdf.setFillColor(16, 185, 129); // emerald-500
  pdf.rect(0, 0, pageWidth, 32, 'F');

  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  pdf.text(dahiraName, 14, 16);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(12);
  pdf.text('Reçu de cotisation', 14, 25);

  // N° de reçu (aligné à droite dans l'en-tête)
  pdf.setFontSize(9);
  pdf.text(`Reçu N° ${contribution.id || '—'}`, pageWidth - 14, 14, { align: 'right' });
  pdf.text(`Émis le ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - 14, 20, { align: 'right' });

  // ---- Corps : informations détaillées ----
  pdf.setTextColor(30, 41, 59); // slate-800
  let y = 48;
  const labelX = 14;
  const valueX = 70;

  const rows = [
    ['Membre', contribution.userName || 'Anonyme'],
    ['Type de cotisation', contribution.type || '-'],
    ['Mode de paiement', paymentLabel(contribution.paymentMethod)],
    ['Date de la cotisation', formatDate(contribution.createdAt)]
  ];

  pdf.setFontSize(11);
  rows.forEach(([label, value]) => {
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 116, 139); // slate-500
    pdf.text(label, labelX, y);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(30, 41, 59);
    pdf.text(String(value), valueX, y);
    y += 10;
  });

  // ---- Montant en gros, encadré ----
  const boxY = y + 4;
  const boxH = 26;
  pdf.setFillColor(236, 253, 245); // emerald-50
  pdf.setDrawColor(16, 185, 129);
  pdf.roundedRect(labelX, boxY, pageWidth - 28, boxH, 3, 3, 'FD');

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(5, 150, 105); // emerald-600
  pdf.text('Montant versé', labelX + 6, boxY + 9);

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(22);
  pdf.setTextColor(5, 150, 105);
  const amount = (contribution.amount || 0).toLocaleString('fr-FR');
  pdf.text(`${amount} FCFA`, pageWidth - 20, boxY + 17, { align: 'right' });

  // ---- Mention de validation ----
  let footerY = boxY + boxH + 12;
  pdf.setFont('helvetica', 'italic');
  pdf.setFontSize(10);
  pdf.setTextColor(71, 85, 105); // slate-600
  const validatedBy = contribution.validatedBy || 'Administration';
  pdf.text(`Validé par ${validatedBy}`, labelX, footerY);

  // ---- Pied de page ----
  pdf.setDrawColor(226, 232, 240); // slate-200
  pdf.line(14, pageHeight - 16, pageWidth - 14, pageHeight - 16);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(148, 163, 184); // slate-400
  pdf.text(
    `${dahiraName} — Ce reçu atteste de la bonne réception de votre cotisation. Qu'Allah vous récompense.`,
    pageWidth / 2,
    pageHeight - 10,
    { align: 'center' }
  );

  // ---- Téléchargement ----
  pdf.save(`recu_cotisation_${contribution.id || 'cotisation'}.pdf`);
};
